import { useState, useEffect, useCallback } from "react";
import { Heart, Menu, X, Facebook } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/0k2pd0j0_rs%3Dw_800%2Ch_800%2Ccg_true%2Cm.webp";
const FB_URL = "https://www.facebook.com/profile.php?id=61579996282258";

const NAV_ITEMS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Caregiver Boxes", href: "/caregiver-boxes" },
  { label: "Caregiver Resources", href: "/caregiver-resources" },
  { label: "Jay's Corner", href: "/jays-corner" },
  { label: "Partnerships", href: "/community-partnerships" },
  { label: "Contact", href: "/contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const showLight = isHome && !scrolled;

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showLight ? "glass-nav" : "glass-nav scrolled"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 no-underline"
          >
            <img
              src={LOGO_URL}
              alt="TYSS Logo"
              className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-contain"
              style={{ filter: showLight ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" : "none" }}
            />
            <div className="flex flex-col">
              <span
                className={`font-serif font-bold text-xl md:text-2xl tracking-tight leading-tight transition-colors duration-300 ${
                  showLight ? "text-white" : "text-earth"
                }`}
                style={{ textShadow: showLight ? "0 1px 4px rgba(0,0,0,0.4)" : "none" }}
              >
                TYSS
              </span>
              <span
                className={`text-[10px] font-sans tracking-wider uppercase transition-colors duration-300 ${
                  showLight ? "text-white/70" : "text-earth-muted"
                }`}
              >
                Healing &amp; Hope
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/['\s]+/g, "-")}`}
                className={`hover-underline text-xs font-sans font-medium tracking-wide transition-colors duration-300 no-underline ${
                  location.pathname === item.href
                    ? showLight ? "text-white" : "text-[#0E8DB5]"
                    : showLight ? "text-white/75 hover:text-white" : "text-earth-muted hover:text-earth"
                }`}
                style={{ textShadow: showLight ? "0 1px 2px rgba(0,0,0,0.3)" : "none" }}
              >
                {item.label}
              </Link>
            ))}
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 no-underline ${
                showLight
                  ? "bg-white/15 text-white hover:bg-white/25"
                  : "bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20"
              }`}
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-donate-button"
              className="btn-pill btn-primary text-xs px-5 py-2.5 no-underline pulse-glow"
            >
              <Heart className="w-3.5 h-3.5" />
              Donate
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden bg-transparent border-none cursor-pointer p-2 transition-colors ${
              showLight ? "text-white" : "text-earth"
            }`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden mobile-menu-enter bg-white/95 backdrop-blur-lg border-t border-[#C8DDD8]">
          <div className="px-6 py-6 flex flex-col gap-3">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/['\s]+/g, "-")}`}
                className={`text-left text-base font-sans font-medium py-1.5 no-underline ${
                  location.pathname === item.href ? "text-[#0E8DB5]" : "text-earth-muted hover:text-earth"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-donate"
              className="btn-pill btn-primary text-center mt-2 no-underline"
            >
              <Heart className="w-4 h-4" />
              Donate Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
